// Utilidades para trabajar con la jerarquía de categorías del módulo admin.
// El endpoint GET /api/admin/categorias/{institucion} devuelve la lista plana
// con `padreId`; aquí se reconstruye el árbol en el cliente.

const ordenarHermanos = (a, b) =>
  (a.orden ?? 0) - (b.orden ?? 0) || a.nombre.localeCompare(b.nombre, 'es');

/**
 * Reordena la lista plana de categorías en recorrido de profundidad (padres
 * seguidos de sus hijos) y agrega a cada una el campo `nivel` (0 = raíz).
 * Las huérfanas (padreId que no está en la lista) se agregan al final como raíz.
 */
export const aplanarCategorias = (categorias) => {
  const porPadre = new Map();
  categorias.forEach((c) => {
    const clave = c.padreId ?? null;
    if (!porPadre.has(clave)) porPadre.set(clave, []);
    porPadre.get(clave).push(c);
  });
  porPadre.forEach((grupo) => grupo.sort(ordenarHermanos));

  const resultado = [];
  const visitadas = new Set();
  const recorrer = (padreId, nivel) => {
    (porPadre.get(padreId) || []).forEach((c) => {
      if (visitadas.has(c.id)) return; // protección ante ciclos padre-hijo
      visitadas.add(c.id);
      resultado.push({ ...c, nivel });
      recorrer(c.id, nivel + 1);
    });
  };
  recorrer(null, 0);

  categorias.forEach((c) => {
    if (!visitadas.has(c.id)) {
      visitadas.add(c.id);
      resultado.push({ ...c, nivel: 0 });
      recorrer(c.id, 1);
    }
  });

  return resultado;
};

/**
 * Devuelve el Set de ids del subárbol de una categoría (ella misma y todos
 * sus descendientes). Útil para excluir opciones inválidas de categoría padre.
 */
export const idsSubarbol = (categorias, id) => {
  const hijosPor = new Map();
  categorias.forEach((c) => {
    if (c.padreId != null) {
      if (!hijosPor.has(c.padreId)) hijosPor.set(c.padreId, []);
      hijosPor.get(c.padreId).push(c.id);
    }
  });

  const ids = new Set();
  const pendientes = [id];
  while (pendientes.length > 0) {
    const actual = pendientes.pop();
    if (ids.has(actual)) continue;
    ids.add(actual);
    (hijosPor.get(actual) || []).forEach((h) => pendientes.push(h));
  }
  return ids;
};

/**
 * Devuelve un Map id → array con los nombres de la ruta completa
 * (desde la raíz hasta la propia categoría).
 */
export const mapaRutasCategorias = (categorias) => {
  const porId = new Map(categorias.map((c) => [c.id, c]));
  const rutas = new Map();
  categorias.forEach((c) => {
    const nombres = [];
    const vistos = new Set();
    let actual = c;
    while (actual && !vistos.has(actual.id)) {
      vistos.add(actual.id);
      nombres.unshift(actual.nombre);
      actual = actual.padreId != null ? porId.get(actual.padreId) : null;
    }
    rutas.set(c.id, nombres);
  });
  return rutas;
};
